import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { Penghasilan } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { formatRupiah } from "@/lib/utils"

const postData = async (newData: Penghasilan) => {
  const response = await api.post("/master/penghasilan/store", newData)
  return response.data
}

const patchData = async (newData: Penghasilan) => {
  const response = await api.patch("/master/penghasilan/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_penghasilan: number }) => {
  const response = await api({
    url: "/master/penghasilan/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: Penghasilan | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<Penghasilan>({
    id_ref_penghasilan: selectedRow?.id_ref_penghasilan || 0,
    penghasilan_start: selectedRow?.penghasilan_start,
    penghasilan_end: selectedRow?.penghasilan_end,
    penghasilan_text: selectedRow?.penghasilan_text || "",
    penghasilan_skor: selectedRow?.penghasilan_skor || 0,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listPenghasilan"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Penghasilan"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.penghasilan_text}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Penghasilan" : "Update Data Penghasilan"}
      />
      <div className="flex gap-3">
        <div>
          <Label>Penghasilan Start</Label>
          <Input
            type="text"
            required
            placeholder="Ketik penghasilan start..."
            value={form.penghasilan_start}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                penghasilan_start: event.target.value,
                penghasilan_text: `${formatRupiah(
                  Number(event.target.value)
                )} - ${
                  prev.penghasilan_end === ""
                    ? ""
                    : formatRupiah(Number(prev.penghasilan_end))
                }`,
              }))
            }
          />
        </div>
        <div>
          <Label>Penghasilan End</Label>
          <Input
            type="text"
            required
            placeholder="Ketik penghasilan end..."
            value={form.penghasilan_end}
            onChange={(event) => {
              if (event.target.value === "~") {
                setForm((prev) => ({
                  ...prev,
                  penghasilan_end: event.target.value,
                  penghasilan_text: `>= ${
                    prev.penghasilan_start === ""
                      ? ""
                      : formatRupiah(Number(prev.penghasilan_start))
                  }`,
                }))
              } else {
                setForm((prev) => ({
                  ...prev,
                  penghasilan_end: event.target.value,
                  penghasilan_text: `${
                    prev.penghasilan_start === ""
                      ? ""
                      : formatRupiah(Number(prev.penghasilan_start))
                  } - ${formatRupiah(Number(event.target.value))}`,
                }))
              }
            }}
          />
        </div>
      </div>
      <div>
        <Label>Penghasilan Text</Label>
        <Input
          required
          placeholder="Ketik penghasilan text..."
          value={form.penghasilan_text}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              penghasilan_text: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Skor</Label>
        <Input
          required
          type="number"
          placeholder="Ketik skor..."
          value={form.penghasilan_skor}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              penghasilan_skor: Number(event.target.value),
            }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
